import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Map, Upload, ArrowLeft, MapPin, AlertCircle, CheckCircle, Trash2, Calendar } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BASE = process.env.REACT_APP_BACKEND_URL;

const NAV_ZOOM = 16;
const ARRIVAL_RADIUS_KM = 0.1;

const haversine = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const AdminInspectionPage = ({ Layout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState(location.state?.openMap ? "map" : "upload");
  const [routes, setRoutes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [routeDate, setRouteDate] = useState(() => new Date().toISOString().slice(0, 10));
  const fileRef = useRef(null);

  const fetchRoutes = useCallback(async () => {
    try {
      const [rRes, oRes] = await Promise.all([
        axios.get(`${API}/admin-inspection/routes`),
        axios.get(`${API}/admin-inspection/orders`),
      ]);
      setRoutes(rRes.data || []);
      setOrders(oRes.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("route_date", routeDate);
      await axios.post(`${API}/admin-inspection/upload-route`, fd);
      fetchRoutes();
    } catch (err) { console.error(err); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const deleteRoute = async (id) => {
    try { await axios.delete(`${API}/admin-inspection/routes/${id}`); fetchRoutes(); } catch {}
  };

  if (view === "map") {
    return (
      <Layout>
        <div style={{ margin: '-1.25rem' }}>
          <AdminRouteMap orders={orders} routeDate={routeDate} onBack={() => setView("upload")} navigate={navigate} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5 max-w-3xl mx-auto" data-testid="admin-inspection-page">
        <div>
          <h1 className="text-xl font-bold text-[#1F2937] flex items-center gap-2">
            <Map className="h-5 w-5 text-[#2F5D8A]" />
            Inspection
          </h1>
          <p className="text-gray-400 text-sm mt-0.5">Private route area — upload and run your own inspection routes</p>
        </div>

        {/* Upload section */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="upload-section">
          <h2 className="text-sm font-bold text-gray-700">Upload Route</h2>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <input type="date" value={routeDate} onChange={e => setRouteDate(e.target.value)} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm" data-testid="route-date-input" />
            </div>
            <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold cursor-pointer transition-colors ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white'}`}>
              <Upload className="h-4 w-4" />
              {uploading ? "Uploading..." : "Upload GPX"}
              <input ref={fileRef} type="file" accept=".gpx" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="gpx-file-input" />
            </label>
          </div>
        </div>

        {/* Open Map button */}
        <button onClick={() => setView("map")} className="w-full py-3.5 bg-[#6B8F62] hover:bg-[#5A7B52] text-white font-bold text-sm rounded-lg flex items-center justify-center gap-2 shadow-sm" data-testid="open-map-btn">
          <Map className="h-5 w-5" /> Open My Route Map
        </button>

        {/* Routes list */}
        <div className="space-y-2" data-testid="routes-list">
          <h2 className="text-sm font-bold text-gray-700">My Routes</h2>
          {routes.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No routes uploaded yet</p>
          ) : (
            routes.map(r => (
              <div key={r.id} className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between" data-testid={`route-${r.route_date}`}>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{r.route_date}</p>
                  <p className="text-xs text-gray-400">{r.filename} — {r.status}</p>
                </div>
                <button onClick={() => deleteRoute(r.id)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-route-${r.route_date}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

/* ======== Admin Route Map — reuses same nav logic as inspector ======== */

const isIOSAdmin = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isAndroidAdmin = () => /Android/.test(navigator.userAgent);

const launchAdminNav = (stop) => {
  if (!stop) return;
  const lat = stop.lat, lng = stop.lon;
  if (isIOSAdmin()) {
    const t = Date.now();
    window.location.href = `comgooglemaps://?daddr=${lat},${lng}&directionsmode=driving`;
    setTimeout(() => { if (Date.now() - t < 1500) window.location.href = `http://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`; }, 800);
  } else if (isAndroidAdmin()) {
    window.location.href = `google.navigation:q=${lat},${lng}&mode=d`;
  } else {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`, '_blank');
  }
};

const AdminRouteMap = ({ orders, routeDate, onBack, navigate }) => {
  const [routeStatus, setRouteStatus] = React.useState("loading");
  const [stops, setStops] = React.useState([]);
  const [currentStop, setCurrentStop] = React.useState(-1);
  const [routeStarted, setRouteStarted] = React.useState(false);
  const mapRef = React.useRef(null);
  const mapInstance = React.useRef(null);
  const markersRef = React.useRef([]);
  const stopsRef = React.useRef([]);
  const routeIdRef = React.useRef(null);
  const progressLineRef = React.useRef(null);

  React.useEffect(() => { stopsRef.current = stops; }, [stops]);

  const saveProgress = (started, stopIdx, advance) => { const rId = routeIdRef.current; if (!rId) return; try { sessionStorage.setItem(`admin_route_${rId}`, JSON.stringify({ routeStarted: started, currentStopIndex: stopIdx, advanceOnReturn: !!advance })); } catch {} };
  const clearProgress = () => { const rId = routeIdRef.current; if (rId) try { sessionStorage.removeItem(`admin_route_${rId}`); } catch {} };

  const matchWaypoint = React.useCallback((name, lat, lon) => {
    if (!orders.length) return null;
    if (name) { const byNum = orders.find(o => o.order_number && o.order_number === name.trim()); if (byNum) return byNum; const byPart = orders.find(o => o.order_number && name.includes(o.order_number)); if (byPart) return byPart; }
    if (lat && lon) { let closest = null, minD = Infinity; orders.forEach(o => { if (o.latitude && o.longitude) { const d = Math.sqrt((lat - parseFloat(o.latitude)) ** 2 + (lon - parseFloat(o.longitude)) ** 2); if (d < minD) { minD = d; closest = o; } } }); if (closest && minD < 0.002) return closest; }
    return null;
  }, [orders]);

  const buildIcon = (idx, total, isCurrent, isDone, isMatched) => {
    const L = window.L; if (!L) return null;
    const isStart = idx === 0, isEnd = idx === total - 1;
    let bg, label, size, bw, shadow;
    if (isCurrent) { bg = '#D1A054'; size = 44; bw = 4; label = isStart ? 'S' : isEnd ? 'E' : `${idx + 1}`; shadow = '0 0 20px rgba(209,160,84,0.8)'; }
    else if (isDone) { bg = '#6B8F62'; size = 24; bw = 2; label = '\u2713'; shadow = '0 1px 3px rgba(0,0,0,0.2)'; }
    else if (isStart) { bg = '#16A34A'; size = 32; bw = 3; label = 'S'; shadow = '0 2px 8px rgba(22,163,74,0.5)'; }
    else if (isEnd) { bg = '#DC2626'; size = 32; bw = 3; label = 'E'; shadow = '0 2px 8px rgba(220,38,38,0.5)'; }
    else { bg = isMatched ? '#2F5D8A' : '#9CA3AF'; size = 24; bw = 2; label = `${idx + 1}`; shadow = '0 1px 3px rgba(0,0,0,0.2)'; }
    const fs = isCurrent ? 16 : (isStart || isEnd) ? 12 : 10;
    return L.divIcon({ className: '', iconSize: [size, size], iconAnchor: [size / 2, size / 2], html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:${fs}px;border:${bw}px solid #fff;box-shadow:${shadow};cursor:pointer">${label}</div>` });
  };

  const drawProgressLine = React.useCallback((cur) => { const L = window.L; if (!L || !mapInstance.current) return; if (progressLineRef.current) { progressLineRef.current.remove(); progressLineRef.current = null; } const ss = stopsRef.current; if (cur > 0 && ss.length > 0) { const coords = ss.slice(0, cur + 1).map(s => [s.lat, s.lon]); if (coords.length > 1) progressLineRef.current = L.polyline(coords, { color: "#6B8F62", weight: 5, opacity: 0.8 }).addTo(mapInstance.current); } }, []);
  const refreshMarkers = React.useCallback((newCur) => { const L = window.L; if (!L || !mapInstance.current) return; const ss = stopsRef.current, total = ss.length; markersRef.current.forEach((m, i) => { const ic = buildIcon(i, total, i === newCur, newCur >= 0 && i < newCur, !!ss[i]?.order); if (ic) m.setIcon(ic); m.setZIndexOffset(i === newCur ? 1000 : i < newCur ? 0 : 500); }); drawProgressLine(newCur); }, [drawProgressLine]);

  React.useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    const L = window.L; if (!L) return;
    const map = L.map(mapRef.current, { zoomControl: false }).setView([42.36, -71.06], 10);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    L.control.zoom({ position: 'topright' }).addTo(map);
    mapInstance.current = map; const allBounds = [];
    axios.get(`${API}/admin-inspection/my-route`, { params: { route_date: routeDate } }).then(res => {
      const route = res.data?.route;
      if (route && route.gpx_url) {
        setRouteStatus("found"); routeIdRef.current = route.id;
        fetch(`${BASE}${route.gpx_url}`).then(r => r.text()).then(gpxText => {
          try {
            const parser = new DOMParser(); const gpxDoc = parser.parseFromString(gpxText, "text/xml");
            const linePoints = [];
            gpxDoc.querySelectorAll("trkpt,rtept").forEach(pt => { const la = parseFloat(pt.getAttribute("lat")), lo = parseFloat(pt.getAttribute("lon")); if (!isNaN(la) && !isNaN(lo)) linePoints.push([la, lo]); });
            if (linePoints.length > 1) L.polyline(linePoints, { color: "#2F5D8A", weight: 3, opacity: 0.35, dashArray: "6, 8" }).addTo(map);
            linePoints.forEach(p => allBounds.push(p));
            const wpts = gpxDoc.querySelectorAll("wpt"); const routeStops = [];
            wpts.forEach(wpt => { const lat = parseFloat(wpt.getAttribute("lat")), lon = parseFloat(wpt.getAttribute("lon")); const name = wpt.querySelector("name")?.textContent || `Stop ${routeStops.length + 1}`; if (!isNaN(lat) && !isNaN(lon)) routeStops.push({ lat, lon, name, order: matchWaypoint(name, lat, lon) }); });
            let restoredStarted = false, restoredStop = -1;
            try { const raw = sessionStorage.getItem(`admin_route_${route.id}`); if (raw) { const sv = JSON.parse(raw); if (sv?.routeStarted) { restoredStarted = true; restoredStop = sv.currentStopIndex; if (sv.advanceOnReturn && restoredStop + 1 < routeStops.length) restoredStop++; sessionStorage.setItem(`admin_route_${route.id}`, JSON.stringify({ routeStarted: true, currentStopIndex: restoredStop, advanceOnReturn: false })); } } } catch {}
            const markers = [], total = routeStops.length;
            routeStops.forEach((stop, idx) => {
              const icon = buildIcon(idx, total, restoredStarted && idx === restoredStop, restoredStarted && restoredStop >= 0 && idx < restoredStop, !!stop.order);
              const marker = L.marker([stop.lat, stop.lon], { icon: icon || undefined }).addTo(map);
              marker.bindTooltip(`<b>${idx === 0 ? 'START' : idx === total - 1 ? 'END' : `#${idx + 1}`}</b> ${stop.order ? stop.order.order_number : stop.name}`, { direction: "top", offset: [0, -20] });
              const si = idx;
              marker.on("click", () => { setCurrentStop(si); setRouteStarted(true); const ss = stopsRef.current; markersRef.current.forEach((mk, mi) => { const ic = buildIcon(mi, ss.length, mi === si, si >= 0 && mi < si, !!ss[mi]?.order); if (ic) mk.setIcon(ic); }); if (progressLineRef.current) { progressLineRef.current.remove(); progressLineRef.current = null; } if (si > 0) { const dc = ss.slice(0, si + 1).map(s => [s.lat, s.lon]); if (dc.length > 1) progressLineRef.current = L.polyline(dc, { color: "#6B8F62", weight: 5, opacity: 0.8 }).addTo(mapInstance.current); } const rId = routeIdRef.current; if (rId) try { sessionStorage.setItem(`admin_route_${rId}`, JSON.stringify({ routeStarted: true, currentStopIndex: si, advanceOnReturn: false })); } catch {} mapInstance.current.setView([ss[si].lat, ss[si].lon], 14, { animate: true }); });
              markers.push(marker); allBounds.push([stop.lat, stop.lon]);
            });
            stopsRef.current = routeStops; markersRef.current = markers; setStops(routeStops);
            if (restoredStarted) { setRouteStarted(true); setCurrentStop(restoredStop); if (restoredStop > 0) { const dc = routeStops.slice(0, restoredStop + 1).map(s => [s.lat, s.lon]); if (dc.length > 1) progressLineRef.current = L.polyline(dc, { color: "#6B8F62", weight: 5, opacity: 0.8 }).addTo(map); } setTimeout(() => { if (restoredStop >= 0 && restoredStop < routeStops.length) map.setView([routeStops[restoredStop].lat, routeStops[restoredStop].lon], 14, { animate: true }); }, 500); }
            else { if (allBounds.length > 0) map.fitBounds(allBounds, { padding: [40, 40] }); }
          } catch (e) { console.error("GPX parse error:", e); }
        }).catch(() => {});
      } else setRouteStatus("none");
    }).catch(() => setRouteStatus("none"));
    return () => { map.remove(); mapInstance.current = null; };
  }, [orders, navigate, matchWaypoint, routeDate]);

  const startRoute = () => { setRouteStarted(true); setCurrentStop(0); refreshMarkers(0); saveProgress(true, 0, false); const ss = stopsRef.current; if (ss[0] && mapInstance.current) mapInstance.current.setView([ss[0].lat, ss[0].lon], 14, { animate: true }); };
  const openOrder = () => { if (currentStop >= 0 && stopsRef.current[currentStop]?.order) { saveProgress(true, currentStop, true); navigate(`/order/${stopsRef.current[currentStop].order.id}?from=inspection`); } };
  const goToStop = (idx) => { if (idx < 0 || idx >= stopsRef.current.length) return; setCurrentStop(idx); refreshMarkers(idx); saveProgress(true, idx, false); if (mapInstance.current) mapInstance.current.setView([stopsRef.current[idx].lat, stopsRef.current[idx].lon], 14, { animate: true }); };
  const endRoute = () => { setRouteStarted(false); setCurrentStop(-1); refreshMarkers(-1); if (progressLineRef.current) { progressLineRef.current.remove(); progressLineRef.current = null; } clearProgress(); if (mapInstance.current && stopsRef.current.length > 0) mapInstance.current.fitBounds(stopsRef.current.map(s => [s.lat, s.lon]), { padding: [40, 40] }); };
  const launchNav = () => launchAdminNav(stopsRef.current[currentStop]);

  const curData = currentStop >= 0 && currentStop < stops.length ? stops[currentStop] : null;
  const hasNext = currentStop + 1 < stops.length;
  const displayName = curData?.order ? curData.order.order_number : (curData?.name || '');
  const displayAddr = curData?.order ? [curData.order.address, curData.order.city, curData.order.state].filter(Boolean).join(', ') : (curData?.name || '');

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 56px)' }} data-testid="admin-route-map">
      <div className="flex-shrink-0">
        {routeStarted && curData ? (<>
          <div className="bg-[#1a1a2e] text-white px-3 py-2 flex items-center gap-3" data-testid="nav-header">
            <div className="w-9 h-9 rounded-full bg-[#D1A054] flex items-center justify-center font-bold text-sm flex-shrink-0">{currentStop + 1}</div>
            <div className="flex-1 min-w-0"><div className="flex items-center gap-1.5">{currentStop === 0 && <span className="px-1 py-0.5 rounded text-[8px] font-bold bg-green-500/20 text-green-400">START</span>}{currentStop === stops.length - 1 && <span className="px-1 py-0.5 rounded text-[8px] font-bold bg-red-500/20 text-red-400">END</span>}<span className="text-sm font-bold truncate">{displayName}</span></div><p className="text-[11px] text-gray-400 truncate">{displayAddr}</p></div>
            <p className="text-[9px] text-gray-400">{currentStop + 1}/{stops.length}</p>
          </div>
          <div className="w-full h-1.5 bg-gray-200"><div className="h-full bg-gradient-to-r from-[#6B8F62] to-[#8CB584] transition-all duration-500" style={{ width: `${((currentStop + 1) / stops.length) * 100}%` }} /></div>
          <div className="bg-white px-3 py-2 border-b border-gray-200 space-y-2" data-testid="route-controls">
            <div className="flex gap-2">
              <button onClick={launchNav} className="flex-1 py-3 bg-[#34A853] text-white text-sm font-bold rounded-lg flex items-center justify-center gap-1.5"><MapPin className="h-4 w-4" /> Navigate</button>
              {curData?.order ? <button onClick={openOrder} className="flex-1 py-3 bg-[#2F5D8A] text-white text-sm font-bold rounded-lg">Open Order</button> : <span className="flex-1 py-3 bg-gray-50 text-gray-400 text-sm font-bold rounded-lg border border-gray-200 text-center">No Order</span>}
            </div>
            <div className="flex gap-2">
              <button disabled={currentStop <= 0} onClick={() => goToStop(currentStop - 1)} className="px-3 py-2.5 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg disabled:opacity-30">Prev</button>
              <div className="flex-1" />
              {hasNext ? <button onClick={() => goToStop(currentStop + 1)} className="px-4 py-2.5 bg-[#D1A054] text-white text-xs font-bold rounded-lg">Next</button> : <button onClick={endRoute} className="px-4 py-2.5 bg-[#C07070] text-white text-xs font-bold rounded-lg">End</button>}
            </div>
          </div>
        </>) : routeStatus === "found" && stops.length > 0 ? (
          <div className="bg-white px-3 py-3 border-b border-gray-200"><button onClick={startRoute} className="w-full py-3.5 bg-[#6B8F62] text-white font-bold text-sm rounded-lg flex items-center justify-center gap-2"><Map className="h-5 w-5" /> Start Route ({stops.length} stops)</button></div>
        ) : null}
      </div>
      <div className="flex-1 relative">
        <div ref={mapRef} className="w-full h-full" data-testid="admin-inspection-map" />
        {routeStatus === "none" && <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg shadow-sm"><p className="text-xs text-amber-700 font-medium">No route for {routeDate}</p></div>}
        <div className="absolute top-2 left-2 z-[1000]"><button onClick={onBack} className="w-9 h-9 bg-white/95 rounded-full shadow-md flex items-center justify-center"><ArrowLeft className="h-4 w-4 text-gray-700" /></button></div>
      </div>
    </div>
  );
};

export default AdminInspectionPage;

